import { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { type SubmitErrorHandler, useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { z } from "zod";

import { toastError } from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useGetIsLegalEntity,
  useGetOwners,
  useGetTradingAccount,
  useMutationRequest,
} from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import { type Owner, patchAccount } from "~/v2/api/rest/account";
import {
  patchOwner,
  postOwner,
  toEntityAccount,
  toIndividualAccount,
} from "~/v2/api/rest/entityOnboarding";
import { OnboardingLayout } from "~/v2/components/onboarding";
import { Path } from "~/v2/path";
import { useCountry } from "../../../../../api/hooks/useCountry";
import {
  DEFAULT_BUSINESS_INFO_VALUES,
  type EntityOnboardingHistoryState,
  REQUIRED_ERR_MSG,
} from "../../constants";
import { usePageTimer } from "../../usePageTimer";
import {
  getDefaultFormValues,
  isAllAsterisks,
  logAmplitudeEvent,
  type Transform,
} from "../../util";
import { BUSINESS_INFO_FAQ } from "./faq";
import { BusinessInfoForm } from "./form";
import { type BusinessInfo, zBusinessInfo } from "./schema";

// Backend values transformed to usable frontend values
const BUSINESS_INFO_TRANSFORMS: [
  Transform<Owner, number | string, number>,
  Transform<Owner, string[], string>,
  Transform<Owner, unknown, boolean>,
  Transform<Owner, unknown, string>,
] = [
  {
    keys: [
      "liquid_net_worth_min",
      "liquid_net_worth_max",
      "total_net_worth_min",
      "total_net_worth_max",
      "annual_income_min",
      "annual_income_max",
    ],
    condition: (value) =>
      value !== "" && value !== null && !Number.isNaN(Number(value as number)),
    transform: (value) => Number(value),
  },
  {
    keys: ["street_address", "mailing_street_address", "funding_source"],
    condition: (value) => Array.isArray(value),
    transform: (value) => value.join(", "),
  },
  {
    keys: ["is_exempt_from_tax_under_501a", "is_participating_fatca"],
    condition: (value) => value == null,
    transform: (_emptyValue) => false,
  },
  {
    keys: "ALL_FIELDS",
    condition: (value) => value == null,
    transform: (_emptyValue) => "",
  },
];

export const BusinessInfoPage = () => {
  const history = useHistory<EntityOnboardingHistoryState>();
  const countryData = useCountry();

  const queryClient = useQueryClient();
  const { account } = useGetTradingAccount();
  const { isLegalEntity } = useGetIsLegalEntity();
  const { owners, isLoading: isOwnersLoading } = useGetOwners({
    accountId: account?.id || "",
    options: { enabled: !!account?.id },
  });

  const entity = useMemo(
    () => owners?.find((details) => details.is_legal_entity),
    [owners],
  );
  // If the account opener returned by useGetOwners has a tax residence they do not need to re-submit a tax id
  const isTaxIDRequired = !entity?.country_of_tax_residence;

  const pageTimer = usePageTimer("entity_onboarding_entity_profile");

  const mutation = useMutationRequest(
    async (data: BusinessInfo) => {
      if (!isLegalEntity) {
        try {
          await toEntityAccount(account?.id ?? "");
        } catch (err) {
          const errorMsg = getErrorMessage(err as Error);
          throw new Error(errorMsg);
        }
      }

      await patchAccount(account?.id ?? "", {
        investment_objective: data.investment_objective,
      });

      try {
        await (entity
          ? patchOwner({
              type: "legal-entity",
              accountId: account?.id ?? "",
              ownerId: entity.owner_id,
              data,
            })
          : postOwner({
              type: "legal-entity",
              accountId: account?.id ?? "",
              data,
            }));
      } catch (err) {
        const errorMsg = getErrorMessage(err as Error);

        if (!entity) {
          try {
            await toIndividualAccount(account?.id ?? "");
          } catch (error) {
            const errorMsg = getErrorMessage(error as Error);
            throw new Error(errorMsg);
          }
        }

        throw new Error(errorMsg);
      }
    },
    {
      onSuccess: () => {
        const duration = pageTimer.stop();
        logAmplitudeEvent.pageCompleted(duration);

        // Invalidate the owners query to get the latest entity profile
        queryClient.invalidateQueries({
          queryKey: queryKeys.documents.owners(account?.id ?? ""),
        });

        // Add history state for better redirects
        history.push(Path.ROUTE_ENTITY_ONBOARDING_ACCOUNT_OPENER, {
          isInEntityOnboarding: true,
        });
      },
      onError: (err) => {
        const errorMsg = getErrorMessage(err);
        toastError(
          errorMsg ?? "There was an error creating entity profile",
          {},
        );
      },
    },
  );

  const form = useForm<BusinessInfo>({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: zodResolver(
      zBusinessInfo.superRefine(
        (
          {
            country_of_tax_residence,
            tax_id_type,
            tax_id,
            country_of_incorporation,
            state_of_incorporation,
            type_of_business,
            type_of_business_other_free_text,
            country,
          },
          refinementContext,
        ) => {
          const countryOfIncorporationAlpha3 =
            countryData.nameToAlpha3(country_of_incorporation) ?? "";

          const countryOfTaxResidenceAlpha3 =
            countryData.nameToAlpha3(country_of_tax_residence) ?? "";

          const entityCountryAlpha3 = countryData.nameToAlpha3(country);

          if (!countryOfIncorporationAlpha3) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Select country from dropdown",
              path: ["country_of_incorporation"],
            });
          }

          if (!countryOfTaxResidenceAlpha3) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Select country from dropdown",
              path: ["country_of_tax_residence"],
            });
          }

          if (!entityCountryAlpha3) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Select country from dropdown",
              path: ["country"],
            });
          }

          const countryTaxIdTypes =
            countryData?.infos[countryOfTaxResidenceAlpha3]?.tax_id_types;

          const taxIdTypeMatch = countryTaxIdTypes?.find(
            ({ id_type }) => id_type === tax_id_type,
          );
          const regexp = taxIdTypeMatch?.regexp;

          // Tax ID Type must be valid for the country
          if (countryTaxIdTypes && !taxIdTypeMatch) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Invalid Tax ID Type",
              path: ["tax_id_type"],
            });
          }

          // Check Tax ID if its required OR if tax id is not all ******
          const isTaxIDCheckRequired =
            isTaxIDRequired || !isAllAsterisks(tax_id ?? "");

          // Tax ID must match the regexp for the tax ID type, if it exists
          if (
            isTaxIDCheckRequired &&
            regexp &&
            !new RegExp(regexp).test(tax_id ?? "")
          ) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Invalid Tax ID",
              path: ["tax_id"],
            });
          }

          // State of Incorporation is required for USA entities
          if (
            countryOfIncorporationAlpha3 === "USA" &&
            !state_of_incorporation
          ) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: REQUIRED_ERR_MSG,
              path: ["state_of_incorporation"],
            });
          }

          if (
            type_of_business === "other" &&
            !type_of_business_other_free_text?.trim()
          ) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: REQUIRED_ERR_MSG,
              path: ["type_of_business_other_free_text"],
            });
          }
        },
      ),
    ),
    defaultValues: getDefaultFormValues(
      entity,
      BUSINESS_INFO_TRANSFORMS as Transform<Owner>[],
      DEFAULT_BUSINESS_INFO_VALUES,
    ),
  });

  const onBackClicked = async () => {
    logAmplitudeEvent.backClicked();

    if (isLegalEntity) {
      try {
        await toIndividualAccount(account?.id ?? "");
      } catch (err) {
        const errorMsg = getErrorMessage(err as Error);
        toastError(
          errorMsg ?? "There was an error converting to individual account",
          {},
        );
        return;
      }
    }

    history.replace(Path.ROUTE_NEW_ACCOUNT, {
      isInEntityOnboarding: undefined,
    });
  };

  const onSubmitError: SubmitErrorHandler<BusinessInfo> = (errs) => {
    toastError("Form has errors. Please check the form and try again.");
    const errorMessage = Object.values(errs)
      .map((e) => e.message)
      .join(", ");
    logAmplitudeEvent.invalidInput(errorMessage);
  };

  const onSubmit = ({
    tax_id,
    country_of_incorporation,
    country_of_tax_residence,
    country,
    ...values
  }: BusinessInfo) => {
    const countryOfIncorporationAlpha3 = countryData.nameToAlpha3(
      country_of_incorporation,
    );
    const countryOfTaxResidenceAlpha3 = countryData.nameToAlpha3(
      country_of_tax_residence,
    );
    const entityCountryAlpha3 = countryData.nameToAlpha3(country);

    if (
      !countryOfIncorporationAlpha3 ||
      !countryOfTaxResidenceAlpha3 ||
      !entityCountryAlpha3
    ) {
      return;
    }

    const isTaxIDIncluded = isTaxIDRequired || !isAllAsterisks(tax_id ?? "");

    logAmplitudeEvent.nextClicked();
    mutation.mutate({
      ...values,
      country_of_incorporation: countryOfIncorporationAlpha3,
      country_of_tax_residence: countryOfTaxResidenceAlpha3,
      country: entityCountryAlpha3,
      ...(isTaxIDIncluded ? { tax_id } : {}),
    });
  };

  // Update form (pre-fill) with existing owner data when it returns
  // biome-ignore lint/correctness/useExhaustiveDependencies: countryData.alpha3ToName is a method on the per-render object returned by the country hook, so listing it would reset the form on every render and discard the user's input.
  useEffect(() => {
    if (entity && !countryData.isLoading) {
      const countryTransform: Transform<
        Owner,
        string | undefined,
        string | undefined
      > = {
        keys: [
          "country_of_incorporation",
          "country_of_tax_residence",
          "country",
        ],
        condition: () => true,
        transform: (value) => countryData.alpha3ToName(value || ""),
      };

      form.reset({
        ...getDefaultFormValues(
          { ...entity },
          [...BUSINESS_INFO_TRANSFORMS, countryTransform] as Transform<Owner>[],
          DEFAULT_BUSINESS_INFO_VALUES,
        ),
        // Only add fake tax id if they are updating an existing entity
        ...(entity?.owner_id && entity?.country_of_tax_residence
          ? { tax_id: "*********" }
          : {}),
      });
    }
  }, [entity, countryData.isLoading, form.reset]);

  // Once an account has multiple owners we cannot convert back to individual account type.
  const isBackBtnHidden = (owners?.length ?? 0) > 1;
  const isLoading = !account || isOwnersLoading;
  const isSubmitDisabled = mutation.isPending;

  return (
    <OnboardingLayout
      title="Business Information"
      percentage={16}
      isLoading={isLoading}
      faq={BUSINESS_INFO_FAQ}
    >
      <BusinessInfoForm
        form={form}
        onSubmit={onSubmit}
        onSubmitError={onSubmitError}
        country={countryData}
        onBackClicked={onBackClicked}
        isBackHidden={isBackBtnHidden}
        isSubmitDisabled={isSubmitDisabled}
        isTaxIDRequired={isTaxIDRequired}
      />
    </OnboardingLayout>
  );
};

export default BusinessInfoPage;
